import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react'
import {
  startToolOperation,
  trackToolRejection,
  type ToolAnalyticsContext,
  type ToolErrorCode,
} from '@saas-pages/shared/public/runtime'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import {
  calculateMarginMarkup,
  marginPairKey,
  MAX_MARGIN_INPUT_LENGTH,
  MONEY_DECIMAL_PLACES,
  PERCENT_DECIMAL_PLACES,
  type MarginCalculation,
  type MarginCalculationError,
  type MarginFieldKey,
  type MarginInputs,
  type MarginPairKey,
} from '@/pages/margin-markup-calculator/margin-markup-calculator'
import { useOnSettled, useSettledValue } from '@/shared/use-settled'
import { marginMarkupCalculatorRoute } from '@/shared/utility-registry'

const FIELD_OPTIONS: Array<{
  key: MarginFieldKey
  label: string
  unit: string
  placeholder: string
}> = [
  { key: 'cost', label: 'Себестоимость', unit: 'ден. ед.', placeholder: '800' },
  { key: 'sale', label: 'Цена продажи', unit: 'ден. ед.', placeholder: '1000' },
  { key: 'profit', label: 'Прибыль', unit: 'ден. ед.', placeholder: '200' },
  { key: 'margin', label: 'Маржа', unit: '%', placeholder: '20' },
  { key: 'markup', label: 'Наценка', unit: '%', placeholder: '25' },
]

const INITIAL_INPUTS: MarginInputs = {
  cost: '800',
  sale: '1000',
  profit: '0',
  margin: '20',
  markup: '25',
}

export function marginAnalyticsContext(pair: MarginPairKey): ToolAnalyticsContext {
  return {
    app: 'calcdeck',
    domain: 'calcdeck.ru',
    route: marginMarkupCalculatorRoute.path,
    utility: `${marginMarkupCalculatorRoute.analyticsKey}.${pair}`,
    processingMode: 'local',
  }
}

export function marginAnalyticsError(error: MarginCalculationError): ToolErrorCode {
  if (error === 'number_too_long') return 'size_limit'
  if (error === 'insufficient_scale') return 'unsupported_input'
  return 'invalid_input'
}

function reportMarginCalculation({ calculation, pair }: {
  calculation: MarginCalculation
  pair: MarginPairKey | null
}): void {
  if (!pair) return
  const context = marginAnalyticsContext(pair)
  if (calculation.status === 'error') {
    trackToolRejection(context, marginAnalyticsError(calculation.error))
    return
  }
  startToolOperation(context).success(0)
}

export function MarginMarkupCalculatorPage() {
  const [firstField, setFirstField] = useState<MarginFieldKey>('cost')
  const [secondField, setSecondField] = useState<MarginFieldKey>('sale')
  const [inputs, setInputs] = useState<MarginInputs>(INITIAL_INPUTS)
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle')

  const report = useMemo(() => ({
    calculation: calculateMarginMarkup(firstField, secondField, inputs),
    pair: marginPairKey(firstField, secondField),
  }), [firstField, inputs, secondField])
  const { calculation } = report

  useEffect(() => setCopyStatus('idle'), [calculation])
  useOnSettled(report, reportMarginCalculation)

  const updateField = useCallback((field: MarginFieldKey) => (
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    setInputs((current) => ({ ...current, [field]: event.target.value }))
  }, [])

  const selectFirst = useCallback((field: MarginFieldKey) => {
    setFirstField(field)
    if (field === secondField) {
      setSecondField(FIELD_OPTIONS.find((option) => option.key !== field)!.key)
    }
  }, [secondField])

  const reset = useCallback(() => {
    setFirstField('cost')
    setSecondField('sale')
    setInputs(INITIAL_INPUTS)
  }, [])

  const copyResult = useCallback(async () => {
    if (calculation.status !== 'success' || !navigator.clipboard) {
      setCopyStatus('error')
      return
    }
    const text = FIELD_OPTIONS.map((option) => {
      const value = calculation.result.values[option.key]
      return `${option.label}: ${value ? `${value.text} ${value.unit}` : 'не определена'}`
    }).join('\n')

    try {
      await navigator.clipboard.writeText(text)
      setCopyStatus('copied')
    } catch {
      setCopyStatus('error')
    }
  }, [calculation])

  const liveError = calculation.status === 'error' ? calculation : null
  const settledError = useSettledValue(liveError)
  const error = liveError === null ? null : settledError
  const result = calculation.status === 'success' ? calculation.result : null

  return (
    <div className="mx-auto max-w-6xl">
      <section className="overflow-hidden rounded-lg border bg-card text-card-foreground shadow">
        <div aria-hidden="true" className="h-1.5 bg-gradient-to-r from-emerald-700 via-lime-500 to-amber-400" />
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.9fr)]">
          <div className="min-w-0 space-y-3 border-b p-4 sm:space-y-5 sm:p-6 lg:border-b-0 lg:border-r">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <FieldSelect
                id="margin-first-field"
                label="Первый показатель"
                value={firstField}
                onChange={selectFirst}
              />
              <FieldSelect
                id="margin-second-field"
                label="Второй показатель"
                value={secondField}
                excluded={firstField}
                onChange={setSecondField}
              />
            </div>

            <div className="grid grid-cols-2 gap-3 sm:gap-4 sm:rounded-md sm:border sm:bg-background/70 sm:p-4 sm:shadow-inner">
              {[firstField, secondField].map((field) => {
                const option = FIELD_OPTIONS.find((item) => item.key === field)!
                return (
                  <NumberField
                    key={field}
                    option={option}
                    value={inputs[field]}
                    invalid={Boolean(error?.invalidFields.includes(field))}
                    onChange={updateField(field)}
                  />
                )
              })}
              <p className="col-span-2 hidden text-xs leading-5 text-muted-foreground sm:block">
                Используйте точку или запятую. Поддерживаются ноль и отрицательные значения;
                смысл таких значений зависит от вашей модели расчёта.
              </p>
              {error && (
                <div
                  id="margin-input-error"
                  role="alert"
                  className="col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                >
                  {errorText(error.error)}
                </div>
              )}
            </div>
          </div>

          <div aria-live="polite" className="bg-background/55 p-4 sm:p-6 lg:min-h-[390px]">
            <div className="text-sm font-medium text-muted-foreground">Результат</div>
            {result ? (
              <>
                <div className="mt-2 grid grid-cols-2 gap-2 sm:mt-4 sm:gap-3">
                  {FIELD_OPTIONS.map((option) => {
                    const value = result.values[option.key]
                    return (
                      <div key={option.key} className="min-w-0 rounded-md border bg-card p-3 shadow-sm">
                        <div className="text-xs text-muted-foreground">{option.label}</div>
                        <output className="mt-1 block break-all font-mono text-xl font-semibold tabular-nums">
                          {value ? `${value.text} ${value.unit}` : 'Не определена'}
                        </output>
                        {value?.rounded && (
                          <div className="mt-1 text-xs text-muted-foreground">Округлено для отображения</div>
                        )}
                      </div>
                    )
                  })}
                </div>
                <div className="mt-4 rounded-md border bg-card p-4">
                  <div className="text-xs font-medium uppercase text-muted-foreground">Основная формула</div>
                  <div className="mt-2 font-mono text-sm leading-6">{result.formula}</div>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <Button variant="outline" onClick={copyResult}>Скопировать результат</Button>
                  <Button variant="ghost" onClick={reset}>Сбросить</Button>
                  <span role="status" className="text-sm text-primary">
                    {copyStatus === 'copied' ? 'Все показатели скопированы.' : ''}
                  </span>
                  <span role="alert" className="text-sm text-destructive">
                    {copyStatus === 'error' ? 'Не удалось скопировать результат.' : ''}
                  </span>
                </div>
              </>
            ) : (
              <p className="mt-2 text-sm leading-6 text-muted-foreground">
                {error ? 'Проверьте исходные данные.' : 'Укажите два показателя: остальные появятся здесь сразу.'}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="mt-8 grid gap-4 md:grid-cols-3">
        <InfoCard title="Маржа" text="Прибыль ÷ цена продажи × 100%. Показывает долю прибыли в выручке." />
        <InfoCard title="Наценка" text="Прибыль ÷ себестоимость × 100%. Показывает прибавку к себестоимости." />
        <InfoCard
          title="Округление"
          text={`Расчёт идёт с точными десятичными дробями. Деньги показываются до ${MONEY_DECIMAL_PLACES}, проценты — до ${PERCENT_DECIMAL_PLACES} знаков.`}
        />
      </section>
      <p className="mt-4 text-xs leading-5 text-muted-foreground">
        Это арифметический инструмент, а не бухгалтерская, налоговая или юридическая консультация.
      </p>
    </div>
  )
}

function FieldSelect({
  id,
  label,
  value,
  excluded,
  onChange,
}: {
  id: string
  label: string
  value: MarginFieldKey
  excluded?: MarginFieldKey
  onChange: (field: MarginFieldKey) => void
}) {
  return (
    <label htmlFor={id}>
      <span className="mb-2 block text-sm font-medium">{label}</span>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value as MarginFieldKey)}
        className="h-11 w-full rounded-md border bg-background px-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-ring/20"
      >
        {FIELD_OPTIONS.filter((option) => option.key !== excluded).map((option) => (
          <option key={option.key} value={option.key}>{option.label}</option>
        ))}
      </select>
    </label>
  )
}

function NumberField({
  option,
  value,
  invalid,
  onChange,
}: {
  option: (typeof FIELD_OPTIONS)[number]
  value: string
  invalid: boolean
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
}) {
  const id = `margin-${option.key}`
  return (
    <label htmlFor={id} className="min-w-0">
      <span className="mb-1 block text-xs text-muted-foreground">{option.label}, {option.unit}</span>
      <input
        id={id}
        inputMode="decimal"
        maxLength={MAX_MARGIN_INPUT_LENGTH}
        value={value}
        placeholder={option.placeholder}
        onChange={onChange}
        aria-invalid={invalid}
        aria-describedby={invalid ? 'margin-input-error' : undefined}
        className={cn(
          'h-11 w-full min-w-0 rounded-md border bg-background px-3 font-mono text-base tabular-nums outline-none transition-colors',
          'focus:border-primary focus:ring-2 focus:ring-ring/20',
          invalid && 'border-destructive focus:border-destructive focus:ring-destructive/20',
        )}
      />
    </label>
  )
}

function errorText(error: MarginCalculationError): string {
  if (error === 'empty_input') return 'Заполните оба выбранных поля.'
  if (error === 'number_too_long') return `Введите не более ${MAX_MARGIN_INPUT_LENGTH} символов в одном поле.`
  if (error === 'singular_percentage') return 'Эта комбинация приводит к делению на ноль. Измените процент или выберите другую пару.'
  if (error === 'undefined_selected_percentage') return 'Выбранный процент не определён при нулевой базе. Укажите ненулевую цену продажи для маржи или ненулевую себестоимость для наценки.'
  if (error === 'insufficient_scale') return 'Маржа и наценка без денежного показателя задают только соотношение, но не суммы. Выберите хотя бы одно денежное значение.'
  if (error === 'same_field') return 'Выберите два разных показателя.'
  return 'Введите обычное число с точкой или запятой, без экспоненты и лишних символов.'
}

function InfoCard({ title, text }: { title: string; text: string }) {
  return (
    <Card className="rounded-lg">
      <CardContent className="pt-6">
        <div className="text-sm font-semibold">{title}</div>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p>
      </CardContent>
    </Card>
  )
}
